// Copyright 2026 - Hewlett Packard Enterprise Company
import React, { useRef, useEffect, useContext, useState } from 'react';
import { Box, Text } from 'grommet';
import { AIGenFill } from '@hpe-design/icons-grommet';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../contexts/AuthContext';
import ChatAnswer from './ChatAnswer';
import ChatQuery from './ChatQuery';
import { ChatContext } from '../../providers/ChatProvider';
import { SidebarContext } from '../../providers/SidebarProvider';
import TextEmphasis from '../TextEmphasis/TextEmphasis';
import MessageLoader from './MessageLoader';

const ChatInsightsContent = () => {
  const auth = useContext(AuthContext);
  const [userName, setUserName] = useState();
  const chatBodyRef = useRef();
  const { conversations, handleFreshQueryClick } = useContext(ChatContext);
  const {
    setInsightsTriggered,
    insightsSessionInitialized,
    setInsightsSessionInitialized,
  } = useContext(SidebarContext);
  const { t } = useTranslation('common');

  useEffect(() => {
    let isMounted = true;

    auth
      .getUserName()
      .then(name => {
        isMounted && setUserName(name);
      })
      .catch(e => {
        if (isMounted) {
          setUserName('');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [auth]);

  const insightsInitStarted = useRef(false);

  // Wait for userName to resolve before sending the insights query so the
  // backend has fully-established user context when its tools execute.
  useEffect(() => {
    if (
      !insightsSessionInitialized &&
      !insightsInitStarted.current &&
      userName !== undefined
    ) {
      insightsInitStarted.current = true;
      setInsightsSessionInitialized(true);
      setInsightsTriggered(false);
      handleFreshQueryClick('Show insights');
    }
  }, [
    insightsSessionInitialized,
    setInsightsSessionInitialized,
    setInsightsTriggered,
    handleFreshQueryClick,
    userName,
  ]);

  const insightsConversation = conversations[0];
  const isLoading =
    !insightsConversation || insightsConversation.answer === 'Loading';

  return (
    <Box
      pad="medium"
      align="start"
      data-testid="chat-insights-content"
      overflow="auto"
      flex="grow"
      fill="vertical"
      responsive={false}
    >
      <Box
        gap="xsmall"
        margin={{ bottom: 'xsmall' }}
        flex={false}
        responsive={false}
      >
        <AIGenFill color="brand" size="xxlarge" />
        <TextEmphasis size="xlarge" margin={{ bottom: '3xsmall' }}>
          {userName
            ? t('chat.chat_insights_greeting', { name: userName })
            : t('chat.chat_insights_greeting_no_name')}
        </TextEmphasis>

        {isLoading ? (
          <Box gap="xsmall">
            <Text size="medium">{t('chat.chat_insights_fetching')}</Text>
            <MessageLoader />
          </Box>
        ) : (
          <ChatAnswer
            query={insightsConversation.query}
            answer={insightsConversation.answer}
            showFollowUpQuestions={insightsConversation.showFollowUpQuestions}
            showFeedback={insightsConversation.showFeedback}
          />
        )}
      </Box>
      {conversations.slice(1).map((exchange, index) => (
        <Box
          key={index}
          width="100%"
          pad={{ vertical: 'xsmall' }}
          margin={{ bottom: 'xsmall' }}
          style={{ maxWidth: '100%' }}
          flex={false}
          responsive={false}
        >
          <div ref={chatBodyRef} />
          <ChatQuery query={exchange.query} />
          <ChatAnswer
            query={exchange.query}
            answer={exchange.answer}
            showFollowUpQuestions={exchange.showFollowUpQuestions}
            showFeedback={exchange.showFeedback}
          />
        </Box>
      ))}
    </Box>
  );
};

export default ChatInsightsContent;
